import { formatINR } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, Pencil, Trash2, ClipboardPen, FolderOpen, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

export const STATUS = {
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function AppointmentTable({ rows, fmtDate, openEdit, openClinical, setDeleteId, setReceipt, confirmAppt, receiptsOnly, testid, perms = {} }) {
  if (!rows.length)
    return <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">No appointments yet.</div>;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <Table data-testid={testid}>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Receipt</TableHead>
              <TableHead>Patient</TableHead>
              <TableHead>Doctor</TableHead>
              <TableHead>Disease</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.id} data-testid="appointment-row-item" className="hover:bg-slate-50">
                <TableCell className="font-mono text-xs text-teal-700">{a.receipt_no}{a.source === "online" && <span className="block text-[10px] text-amber-700 font-sans" title={a.reference_id}>Online · {a.reference_id}</span>}</TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900">{a.patient_name}</div>
                  <div className="text-xs text-slate-400">{a.patient_age} yrs</div>
                </TableCell>
                <TableCell className="text-sm">{a.doctor_name}</TableCell>
                <TableCell className="text-sm text-slate-600">{a.disease_name || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(a.appointment_date)}</TableCell>
                <TableCell className="font-mono text-sm">{formatINR(a.amount)}</TableCell>
                <TableCell><Badge variant="outline" className={`capitalize ${STATUS[a.status] || ""}`}>{a.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Link to={`/appointments/${a.id}`} data-testid="view-patient-details-link" title="View full patient record">
                      <Button size="icon" variant="ghost"><FolderOpen className="h-4 w-4" /></Button>
                    </Link>
                    <Button data-testid="view-receipt-button" size="icon" variant="ghost" onClick={() => setReceipt(a)} title="View receipt"><Eye className="h-4 w-4" /></Button>
                    {!receiptsOnly && perms.update && a.status === "pending" && (
                      <Button data-testid="confirm-appointment-button" size="sm" onClick={() => confirmAppt(a)} className="h-8 bg-teal-600 hover:bg-teal-700 text-white"><CheckCircle2 className="h-4 w-4 mr-1" /> Confirm</Button>
                    )}
                    {!receiptsOnly && perms.update && (
                      <Button data-testid="edit-appointment-button" size="icon" variant="ghost" onClick={() => openEdit(a)} title="Edit"><Pencil className="h-4 w-4" /></Button>
                    )}
                    {!receiptsOnly && !perms.update && perms.clinical && (
                      <Button data-testid="clinical-note-button" size="icon" variant="ghost" onClick={() => openClinical(a)} title="Add diagnosis / prescription"><ClipboardPen className="h-4 w-4" /></Button>
                    )}
                    {!receiptsOnly && perms.delete && (
                      <Button data-testid="delete-appointment-button" size="icon" variant="ghost" onClick={() => setDeleteId(a.id)} title="Delete" className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
